import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Handshake, Plus, Trash2, Edit3, Check, X, Loader2,
  Megaphone, Eye, EyeOff, Mail, Building2, Globe, CalendarDays
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  reviewed: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
};

const PartnersTab = ({ headers }) => {
  const [view, setView] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAnn, setEditingAnn] = useState(null);
  const [annForm, setAnnForm] = useState({ title: '', summary: '', content: '', partner_name: '', tier: '', status: 'draft', publish_at: '', source_url: '', image_url: '', announced_by: 'Ghadeer Ayoub, Head of Client Management, Taxera Technologies GmbH' });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appRes, annRes] = await Promise.all([
        fetch(`${API}/api/admin/partners/applications`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/partners/announcements`, { headers, credentials: 'include' }),
      ]);
      const appData = await appRes.json();
      const annData = await annRes.json();
      setApplications(appData.applications || []);
      setAnnouncements(annData.announcements || []);
    } catch (e) {
      console.error('Failed to fetch partners data:', e);
    }
    setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only


  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchData(); }, []);

  const updateAppStatus = async (email, status) => {
    await fetch(`${API}/api/admin/partners/applications/${encodeURIComponent(email)}`, {
      method: 'PUT', headers, credentials: 'include', body: JSON.stringify({ status }),
    });
    fetchData();
  };

  const saveAnnouncement = async () => {
    const method = editingAnn ? 'PUT' : 'POST';
    const url = editingAnn
      ? `${API}/api/admin/partners/announcements/${editingAnn}`
      : `${API}/api/admin/partners/announcements`;
    await fetch(url, { method, headers, credentials: 'include', body: JSON.stringify(annForm) });
    setShowForm(false);
    setEditingAnn(null);
    setAnnForm({ title: '', summary: '', content: '', partner_name: '', tier: '', status: 'draft' });
    fetchData();
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    await fetch(`${API}/api/admin/partners/announcements/${id}`, { method: 'DELETE', headers, credentials: 'include' });
    fetchData();
  };

  const openEdit = (ann) => {
    setEditingAnn(ann.id);
    setAnnForm({ title: ann.title, summary: ann.summary, content: ann.content, partner_name: ann.partner_name, tier: ann.tier, status: ann.status, publish_at: ann.publish_at || '', source_url: ann.source_url || '', image_url: ann.image_url || '', announced_by: ann.announced_by || 'Ghadeer Ayoub, Head of Client Management, Taxera Technologies GmbH' });
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-taxera-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partners-admin-tab">
      {/* Sub-nav */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
        <button
          onClick={() => setView('applications')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            view === 'applications' ? 'bg-taxera-charcoal text-white' : 'text-slate-500 hover:bg-slate-100'
          }`}
          data-testid="partners-view-applications"
        >
          <Handshake className="w-4 h-4 inline mr-1.5" />
          Applications ({applications.length})
        </button>
        <button
          onClick={() => setView('announcements')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            view === 'announcements' ? 'bg-taxera-charcoal text-white' : 'text-slate-500 hover:bg-slate-100'
          }`}
          data-testid="partners-view-announcements"
        >
          <Megaphone className="w-4 h-4 inline mr-1.5" />
          Announcements ({announcements.length})
        </button>
      </div>

      {/* ─── Applications View ─── */}
      {view === 'applications' && (
        <div className="space-y-3">
          {applications.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Handshake className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No partner applications yet</p>
            </div>
          ) : (
            applications.map((app) => (
              <motion.div
                key={app.email}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-sm transition-all"
                data-testid={`partner-app-${app.email}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <h4 className="font-semibold text-taxera-charcoal truncate">{app.company_name}</h4>
                      <span className={`text-2xs font-bold uppercase px-2 py-0.5 rounded-full ${STATUS_COLORS[app.status] || 'bg-slate-100 text-slate-600'}`}>
                        {app.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-slate-400 mb-2">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{app.email}</span>
                      <span>{app.contact_name}</span>
                      {app.phone && <span>{app.phone}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="px-2 py-0.5 bg-taxera-blue/10 text-taxera-blue rounded-full font-semibold">{app.tier}</span>
                      {app.regions && <span className="flex items-center gap-1"><Globe className="w-3 h-3 text-slate-400" />{app.regions}</span>}
                      {app.sap_practice && <span className="text-green-600 font-medium">SAP Practice</span>}
                    </div>
                    {app.message && <p className="mt-2 text-xs text-slate-500 leading-relaxed">{app.message}</p>}
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {app.status === 'new' && (
                      <>
                        <button
                          onClick={() => updateAppStatus(app.email, 'approved')}
                          className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                          title="Approve"
                          data-testid={`approve-${app.email}`}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => updateAppStatus(app.email, 'declined')}
                          className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                          title="Decline"
                          data-testid={`decline-${app.email}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {app.status !== 'new' && app.status !== 'approved' && (
                      <button
                        onClick={() => updateAppStatus(app.email, 'approved')}
                        className="p-2 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors text-xs"
                        data-testid={`approve-${app.email}`}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* ─── Announcements View ─── */}
      {view === 'announcements' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => { setShowForm(true); setEditingAnn(null); setAnnForm({ title: '', summary: '', content: '', partner_name: '', tier: '', status: 'draft', source_url: '', image_url: '', announced_by: 'Ghadeer Ayoub, Head of Client Management, Taxera Technologies GmbH' }); }}
              className="inline-flex items-center gap-2 px-4 py-2.5 taxera-gradient text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
              data-testid="new-announcement-btn"
            >
              <Plus className="w-4 h-4" /> New Announcement
            </button>
          </div>

          {/* Announcement Form */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-50 rounded-xl border border-slate-200 p-5 space-y-4"
              data-testid="announcement-form"
            >
              <h3 className="font-semibold text-taxera-charcoal text-sm">
                {editingAnn ? 'Edit Announcement' : 'New Announcement'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <textarea
                  rows={2}
                  placeholder="Announcement title *"
                  value={annForm.title}
                  onChange={(e) => { setAnnForm({ ...annForm, title: e.target.value }); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                  className="px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 resize-none overflow-hidden"
                  data-testid="ann-title-input"
                />
                <input
                  type="text"
                  placeholder="Partner name (e.g., Amadeus)"
                  value={annForm.partner_name}
                  onChange={(e) => setAnnForm({ ...annForm, partner_name: e.target.value })}
                  className="px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                  data-testid="ann-partner-input"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <select
                  value={annForm.tier}
                  onChange={(e) => setAnnForm({ ...annForm, tier: e.target.value })}
                  className="px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                  data-testid="ann-tier-select"
                >
                  <option value="">Partnership tier (optional)</option>
                  <option value="Referral">Referral</option>
                  <option value="Reseller">Reseller</option>
                  <option value="System Integrator">System Integrator</option>
                  <option value="Strategic Alliance">Strategic Alliance</option>
                </select>
                <select
                  value={annForm.status}
                  onChange={(e) => setAnnForm({ ...annForm, status: e.target.value, ...(e.target.value !== 'scheduled' ? { publish_at: '' } : {}) })}
                  className="px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                  data-testid="ann-status-select"
                >
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="published">Published (now)</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              {annForm.status === 'scheduled' && (
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-4 h-4 text-taxera-blue shrink-0" />
                  <input
                    type="datetime-local"
                    value={annForm.publish_at ? annForm.publish_at.slice(0, 16) : ''}
                    onChange={(e) => setAnnForm({ ...annForm, publish_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                    className="flex-1 px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                    data-testid="ann-publish-at-input"
                  />
                  <span className="text-xs text-slate-400">Publishes automatically at this date & time</span>
                </div>
              )}
              <textarea
                rows={2}
                placeholder="Short summary (shown in the feed)"
                value={annForm.summary}
                onChange={(e) => { setAnnForm({ ...annForm, summary: e.target.value }); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 resize-none overflow-hidden"
                data-testid="ann-summary-input"
              />
              <textarea
                rows={10}
                placeholder="Full announcement content — use blank lines between paragraphs, wrap quotes in quotation marks"
                value={annForm.content}
                onChange={(e) => { setAnnForm({ ...annForm, content: e.target.value }); e.target.style.height = 'auto'; e.target.style.height = Math.max(240, e.target.scrollHeight) + 'px'; }}
                className="w-full px-4 py-3 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20 resize-y min-h-[240px] leading-relaxed"
                data-testid="ann-content-input"
              />
              <input
                type="url"
                placeholder="Partner URL (link to partner's website or their announcement)"
                value={annForm.source_url}
                onChange={(e) => setAnnForm({ ...annForm, source_url: e.target.value })}
                className="w-full px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                data-testid="ann-source-url-input"
              />
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="url"
                  placeholder="Image URL (hero image for the announcement)"
                  value={annForm.image_url}
                  onChange={(e) => setAnnForm({ ...annForm, image_url: e.target.value })}
                  className="px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                  data-testid="ann-image-url-input"
                />
                <input
                  type="text"
                  placeholder="Announced by (e.g., Ghadeer Ayoub, Head of Client Management)"
                  value={annForm.announced_by}
                  onChange={(e) => setAnnForm({ ...annForm, announced_by: e.target.value })}
                  className="px-4 py-2.5 bg-white rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-taxera-blue/20"
                  data-testid="ann-announced-by-input"
                />
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={saveAnnouncement}
                  disabled={!annForm.title}
                  className="px-5 py-2.5 taxera-gradient text-white rounded-xl text-sm font-semibold disabled:opacity-50"
                  data-testid="ann-save-btn"
                >
                  {editingAnn ? 'Update' : 'Create'}
                </button>
                <button
                  onClick={() => { setShowForm(false); setEditingAnn(null); }}
                  className="px-5 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          {/* Announcements List */}
          {announcements.length === 0 && !showForm ? (
            <div className="text-center py-12 text-slate-400">
              <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No announcements yet. Create your first partner announcement.</p>
            </div>
          ) : (
            announcements.map((ann) => (
              <div
                key={ann.id}
                className="bg-white rounded-xl border border-slate-200 p-5 flex items-start justify-between gap-4"
                data-testid={`admin-ann-${ann.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-taxera-charcoal text-sm truncate">{ann.title}</h4>
                    {ann.status === 'published' ? (
                      <span className="flex items-center gap-1 text-2xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                        <Eye className="w-3 h-3" /> Live
                      </span>
                    ) : ann.status === 'scheduled' ? (
                      <span className="flex items-center gap-1 text-2xs font-bold text-taxera-blue bg-taxera-blue/10 px-2 py-0.5 rounded-full">
                        <CalendarDays className="w-3 h-3" /> Scheduled
                      </span>
                    ) : ann.status === 'archived' ? (
                      <span className="flex items-center gap-1 text-2xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                        <EyeOff className="w-3 h-3" /> Archived
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-2xs font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                        <EyeOff className="w-3 h-3" /> Draft
                      </span>
                    )}
                    {ann.tier && (
                      <span className="text-2xs text-taxera-blue bg-taxera-blue/10 px-2 py-0.5 rounded-full font-semibold">{ann.tier}</span>
                    )}
                  </div>
                  {ann.partner_name && <p className="text-xs text-slate-400 mb-1">with {ann.partner_name}</p>}
                  <p className="text-xs text-slate-500 line-clamp-2">{ann.summary || ann.content}</p>
                  {ann.status === 'scheduled' && ann.publish_at && (
                    <p className="text-2xs text-taxera-blue mt-1 flex items-center gap-1 font-medium">
                      <CalendarDays className="w-3 h-3" />
                      Publishes {new Date(ann.publish_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                  {ann.status === 'published' && ann.published_at && (
                    <p className="text-2xs text-slate-300 mt-1 flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />
                      Published {new Date(ann.published_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => openEdit(ann)}
                    className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-taxera-blue transition-colors"
                    title="Edit"
                    data-testid={`edit-ann-${ann.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteAnnouncement(ann.id)}
                    className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    title="Delete"
                    data-testid={`delete-ann-${ann.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default PartnersTab;
